class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.isAddingToCart = false; // Flag to prevent closing during ATC operation

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      // Don't close if user is adding to cart
      if (this.isAddingToCart) return;
      
      if (!this.contains(document.activeElement)) {
        this.close();
      }
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources)
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;

    // NEW: lock body + add overlay class on documentElement if needed
    document.body.classList.add('overflow-hidden');
    document.documentElement.classList.add('predictive-search-open');
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');

    // NEW: unlock body
    document.body.classList.remove('overflow-hidden');
    document.documentElement.classList.remove('predictive-search-open');
  }
}

customElements.define('predictive-search', PredictiveSearch);

// Predictive search product "ADD" buttons -> add item + publish cartUpdate so drawer refreshes
// Predictive search product "ADD" buttons
document.addEventListener('click', async function (e) {
  const btn = e.target.closest('.predictive-atc-btn');
  if (!btn) return;

  e.preventDefault();
  e.stopPropagation();

  if (btn.classList.contains('is-loading') || btn.disabled) return;

  const variantId = btn.dataset.variantId;
  if (!variantId) {
    console.warn('Predictive ATC: missing data-variant-id');
    return;
  }

  // Find the parent predictive-search element and set flag
  const predictiveSearch = btn.closest('predictive-search');
  if (predictiveSearch) {
    predictiveSearch.isAddingToCart = true;
  }

  btn.classList.add('is-loading');
  btn.disabled = true;

  try {
    const body = JSON.stringify({
      items: [
        {
          id: Number(variantId),
          quantity: 1
        }
      ]
    });

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body
    });

    if (!response.ok) {
      console.error('Predictive ATC failed', await response.text());
      throw new Error('Add failed');
    }

    await response.json(); // we don't rely on sections here

    // 1) Button UI feedback - show ADDED state
    const textEl = btn.querySelector('.btn-text');
    const originalText = textEl ? textEl.textContent : '';
    if (textEl) textEl.textContent = 'ADDED';

    // 2) Force-refresh cart drawer - using same approach as cart-drawer.renderContents()
    try {
      const res = await fetch(`${routes.cart_url}?section_id=cart-drawer`);
      if (res.ok) {
        const responseText = await res.text();
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        
        // Update the entire CartDrawer content (handles both empty and non-empty states)
        const targetDrawer = document.querySelector('#CartDrawer');
        const sourceDrawer = html.querySelector('#CartDrawer');
        if (targetDrawer && sourceDrawer) {
          targetDrawer.innerHTML = sourceDrawer.innerHTML;
        }
      }

      // Update cart icon bubble (cart count in header)
      const iconRes = await fetch(`${routes.cart_url}?section_id=cart-icon-bubble`);
      if (iconRes.ok) {
        const iconText = await iconRes.text();
        const iconHtml = new DOMParser().parseFromString(iconText, 'text/html');
        const targetIcon = document.querySelector('#cart-icon-bubble');
        const sourceIcon = iconHtml.querySelector('#cart-icon-bubble');
        if (targetIcon && sourceIcon) {
          targetIcon.innerHTML = sourceIcon.innerHTML;
        }
      }

      // Remove is-empty class from cart drawer and related elements
      const drawer = document.querySelector('cart-drawer');
      if (drawer) {
        const drawerInner = drawer.querySelector('.drawer__inner');
        drawer.classList.remove('is-empty');
        if (drawerInner) drawerInner.classList.remove('is-empty');
        
        // Re-attach overlay click listener after innerHTML update
        const overlay = drawer.querySelector('#CartDrawer-Overlay');
        if (overlay) {
          overlay.addEventListener('click', () => {
            if (drawer && typeof drawer.close === 'function') {
              drawer.close();
            }
          });
        }
      }
      
    } catch (refreshErr) {
      console.error('Predictive ATC drawer refresh error', refreshErr);
    }

    // 3) Open side cart
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
    }

    // 4) Close predictive search overlay AFTER cart drawer opens
    setTimeout(() => {
      if (predictiveSearch) {
        predictiveSearch.close();
        predictiveSearch.isAddingToCart = false;
      }
    }, 300);

    // 5) Reset button state after delay
    setTimeout(() => {
      if (textEl) textEl.textContent = originalText;
      btn.classList.remove('is-loading');
      btn.disabled = false;
    }, 1500);
  } catch (err) {
    console.error('Predictive ATC error', err);
    btn.classList.remove('is-loading');
    btn.disabled = false;
    // Reset flag on error
    if (predictiveSearch) {
      predictiveSearch.isAddingToCart = false;
    }
  }
});

